import styles from './ReferAFriendBanner.module.css'
import ReferralIllustration from '@/components/SpotIllustration/ReferralIllustration'
import { StyledLink } from '@/components/Link'
import { observer } from 'mobx-react-lite'
import { useFeatures } from '@/contexts/root-store-context'
import { useScopedViewModel } from '@/hooks/view-model-hooks'
import {
  ModalDialog,
  ModalDialogActions,
  ModalDialogContent,
  ModalDialogDescription,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogTitle,
} from '@/components/ModalDialog'
import { TextField, TextFieldSlot } from '@/components/inputs/TextField'
import { IconButton } from '@/components/IconButton'
import { IsSkeletonContext } from '@/components/Skeleton'
import { Button } from '@/components/Button'
import { ReferAFriendViewModel } from '@/features/billing/referrals/ReferAFriendBanner/ReferAFriendViewModel'

/**
 * The ReferAFriendBanner
 */
export const ReferAFriendBanner = observer(function ReferAFriendBanner() {
  const { getCurrentWorkspace } = useFeatures(({ iam }) => ({
    getCurrentWorkspace: iam.getCurrentWorkspace,
  }))

  const workspace = getCurrentWorkspace()
  const vm = useScopedViewModel(({ iam, billing, shell }, needs) => {
    const { internal } = needs({ internal: billing.internal() })
    return new ReferAFriendViewModel(
      internal.billingProfileStore,
      shell.newSnackbar,
      iam.getCurrentMember,
      (text) => navigator.clipboard.writeText(text),
    )
  })

  //  Don't render the banner if referral coupons are disabled.
  if (!workspace.config.billing.coupons.referral.enabled) {
    return null
  }

  const amount = workspace.config.billing.coupons.referral.amount
  return (
    <>
      <aside className={styles.referBox}>
        <div className="flex-auto">
          <h3 className="text-headline-md mb-sm">
            Give ${amount}, get ${amount}
          </h3>
          <p className="text-body-sm mb-md">Share the Taxfyle love</p>
          <p>
            <StyledLink onPress={vm.open}>Refer a friend -&gt;</StyledLink>
          </p>
        </div>
        <div className="flex-none">
          <ReferralIllustration />
        </div>
      </aside>
      <ReferAFriendDialog amount={amount} vm={vm} testId={undefined} />
    </>
  )
})

interface ReferAFriendDialogProps {
  vm: ReferAFriendViewModel
  amount: number
  testId?: string
  copyButtonTestId?: string
  closeButtonTestId?: string
}

/**
 * Refer A Friend Dialog
 */
export const ReferAFriendDialog = observer(function ReferAFriendDialog({
  vm,
  amount,
  testId,
  copyButtonTestId,
  closeButtonTestId,
}: ReferAFriendDialogProps) {
  return (
    <IsSkeletonContext value={vm.fetchCode.pending}>
      <ModalDialog
        isOpen={vm.modal.showing}
        onClose={vm.modal.close}
        data-test={testId}
      >
        <ModalDialogHeader>
          <ModalDialogTitle>Give $20, get $20</ModalDialogTitle>
          <ModalDialogDescription>
            Give your friends ${amount} off their tax return and after they
            file, you'll get ${amount} off, too!
          </ModalDialogDescription>
        </ModalDialogHeader>
        <ModalDialogContent spacing="compact">
          <TextField readOnly value={vm.code}>
            <TextFieldSlot side="right">
              <IconButton
                name="copy"
                onPress={vm.copyCodeToClipboard}
                label="Copy referral code"
              />
            </TextFieldSlot>
          </TextField>
        </ModalDialogContent>
        <ModalDialogFooter>
          <ModalDialogActions>
            <Button
              label="Copy code"
              onPress={vm.copyCodeToClipboard}
              data-test={copyButtonTestId}
            />
            <Button
              label="Close"
              variant="outline"
              onPress={vm.modal.close}
              data-test={closeButtonTestId}
            />
          </ModalDialogActions>
        </ModalDialogFooter>
      </ModalDialog>
    </IsSkeletonContext>
  )
})
